import { motion } from "framer-motion";
import { useParams, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import {
  Mail,
  Phone,
  MapPin,
  GraduationCap,
  BookOpen,
  Globe,
  Linkedin,
  Facebook,
  Home,
} from "lucide-react";

export default function FacultyProfile({   
  accent = "#115e32",
  pageTitle = "Faculty Profile | University of Creative Technology Chittagong",
}) {
	const { id } = useParams(); 
	const [faculty, setFaculty] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const facultyPost = async () => {
		setLoading(true);
		setError(null);

		try {
			const payload = {
				userId: import.meta.env.VITE_API_USER,
				password: import.meta.env.VITE_API_PASS,	
				teacher_id:id,
		    };
			const api = axios.create({
				baseURL: import.meta.env.VITE_API_BASE_URL,
				headers: { "Content-Type": "application/json" },
			});
			const response = await api.post("/teacher-info", payload);			
		
			// Loop data (array)
			setFaculty(response.data[0]);			
		} catch (err) {
		   setError(err.message || "Something went wrong");		   
		} finally {
		  setLoading(false);
		}
	};
	useEffect(() => {		
		facultyPost();
	}, []);  
	useEffect(() => {
		document.title = pageTitle;
	}, [pageTitle]);
  if (loading) {
    return (
      <div className="min-h-screen flex justify-center items-center text-amber-800 font-semibold">
        Loading faculty details...
      </div>
    );
  }

  if (!faculty) {
    return (
      <div className="min-h-screen flex justify-center items-center text-[#015A7A] font-medium">
        Faculty not found.
      </div>
    );
  }
	function textformat(textdata){
		let textinfo="";
		textinfo=textdata.replace(/\\n/g, '<br />');
		textinfo=textinfo.split('\\n').join('\n');
		textinfo=textinfo.split('\n').join('<br />');
		textinfo=textinfo.split('\\r').join(' ');
		textinfo=textinfo.split('\\').join(' ');
		return textinfo;
	}
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#B7EAFC] via-[#F0FCFF] to-[#F0FCFF]">
      {/* Hero Section */}
      <section className="relative text-center py-20 overflow-hidden">
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative z-10"
        >
          <div className="flex justify-center mb-6">
            <GraduationCap className="w-16 h-16 text-[#015A7A]" />
          </div>
          <h1 className="text-4xl md:text-5xl font-extrabold text-[#015A7A] mb-3">
            Faculty Profile
          </h1>
          <p className="text-[#015A7A] text-lg max-w-2xl mx-auto">
            Explore professional details, academic background, and research
            contributions.
          </p>
        </motion.div>

        {/* SVG Wave */}
        <svg
          className="absolute bottom-0 left-0 w-full h-24 text-yellow-200"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 1440 320"
        >
          <path
            fill="#6BCCF2"
            d="M0,160L48,144C96,128,192,96,288,122.7C384,149,480,235,576,266.7C672,299,768,277,864,229.3C960,181,1056,107,1152,90.7C1248,75,1344,117,1392,138.7L1440,160V320H0Z"
          ></path>
        </svg>
      </section>

      {/* Faculty Info Card */}
      <section className="max-w-6xl mx-auto px-6 py-10">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="bg-white shadow-lg rounded-3xl border border-amber-100 p-8 md:flex items-center"
        >
          <img
            src={`${import.meta.env.VITE_API_SOURCE_URL}/${faculty.picture}`}
            alt={faculty.name}
            className="w-56 h-56 object-cover rounded-2xl shadow-md border border-[#6BCCF2] mx-auto md:mx-0"
          />

          <div className="md:ml-8 mt-6 md:mt-0 text-[#015A7A] space-y-2">
            <h2 className="text-3xl font-bold">{faculty.name}</h2>
            <p className="text-lg font-semibold text-[#015A7A]">
              {faculty.designation_name}
            </p>
            <p className="flex text-sm text-[#01364C] gap-2"><Home />{faculty.department_name}</p>
			<p className="flex text-sm text-[#01364C] gap-2"><GraduationCap /> {faculty.education}</p>
            <div className="text-sm mt-3 space-y-1">
              <p className="flex items-center gap-2">
                <Phone size={16} /> {faculty.mobile_number}
              </p>
              <p className="flex items-center gap-2">
                <Mail size={16} /> {faculty.email}  {faculty.university_email && (
				  <span>, {faculty.university_email}</span>
				)}
              </p>              
              <p className="flex items-center gap-2">
                <MapPin size={16} /> {textformat(faculty.present_address)}
              </p>
            </div>

            {/* Social / Academic Links */}
            <div className="flex gap-3 mt-4 flex-wrap">
              
                <a
                  href={faculty.google_scholar}
                  target="_blank"
                  className="bg-[#015A7A] text-white px-3 py-1.5 rounded-full text-sm font-semibold hover:bg-amber-800 transition-all"
                >
                  Google Scholar
                </a>
             
              
                <a
                  href={faculty.researchgate}
                  target="_blank"
                  className="bg-[#015A7A] text-white px-3 py-1.5 rounded-full text-sm font-semibold hover:bg-amber-800 transition-all"
                >
                  ResearchGate
                </a>
              
             
                <a
                  href={faculty.orcid}
                  target="_blank"
                  className="bg-[#015A7A] text-white px-3 py-1.5 rounded-full text-sm font-semibold hover:bg-amber-800 transition-all"
                >
                  ORCID
                </a>
             
              
                <a
                  href={faculty.linkedin}
                  target="_blank"
                  className="flex items-center gap-1 bg-blue-700 text-white px-3 py-1.5 rounded-full text-sm font-semibold hover:bg-blue-800 transition-all"
                >
                  <Linkedin size={16} /> LinkedIn
                </a>
             
              
                <a
                  href={faculty.facebook}
                  target="_blank"
                  className="flex items-center gap-1 bg-blue-600 text-white px-3 py-1.5 rounded-full text-sm font-semibold hover:bg-blue-700 transition-all"
                >
                  <Facebook size={16} /> Facebook
                </a>
              
            </div>
          </div>
        </motion.div>
      </section>

      {/* Academic Details Section */}
      <section className="max-w-6xl mx-auto px-6 py-10 space-y-10">
        {[
          { title: "Overview", key: "overview" },
          { title: "Education", key: "education" },
          { title: "Detailed Education", key: "details_education" },
          { title: "Research", key: "research" },
          { title: "Publications", key: "publication" },
          { title: "Conference Publications", key: "conference_publication" },
          { title: "Books", key: "book" },
          { title: "Others", key: "others" },
          { title: "Areas of Interest", key: "area_interest" },
          { title: "Courses Taught", key: "taught" },
          { title: "Experience", key: "experience" },
          { title: "Major", key: "major" },
          { title: "Projects & Awards", key: "project_award" },
        ].map(
          (section, index) =>
            faculty[section.key] && (
              <motion.div
                key={section.key}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.05 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl shadow-md border border-amber-100 p-6"
              >
                <h3 className="text-2xl font-bold text-[#015A7A] mb-3">
                  {section.title}
                </h3>
                <p
                  className="text-[#016C94] leading-relaxed whitespace-pre-line"
                  dangerouslySetInnerHTML={{
                    __html: textformat(faculty[section.key]),
                  }}
                ></p>
              </motion.div>
            )
        )}
      </section>

      <footer className="text-center py-10 text-sm text-[#015A7A]">
        © {new Date().getFullYear()} University of Creative Technology Chittagong
      </footer>
    </div>
  );
}
